import React, { useState, useEffect } from 'react';
import type { ImageFile } from "../App";

interface EditModalProps {
  image: ImageFile;
  isOpen: boolean;
  onClose: () => void;
  onSave: (url: string) => void;
}

const backgroundOptions = [
  { id: 'color', label: '纯色', icon: 'fa-fill-drip' },
  { id: 'pattern', label: '纹理', icon: 'fa-shapes' },
  { id: 'image', label: '图片', icon: 'fa-image' }
];

const effectOptions = [
  { id: 'none', label: '无', icon: 'fa-ban' },
  { id: 'blur', label: '模糊', icon: 'fa-droplet-slash' },
  { id: 'brightness', label: '亮度', icon: 'fa-sun' },
  { id: 'contrast', label: '对比度', icon: 'fa-circle-half-stroke' },
  { id: 'filter', label: '滤镜', icon: 'fa-photo-film' }
];

const filterOptions = [
  { id: 'none', label: '无' },
  { id: 'sepia', label: '怀旧' },
  { id: 'grayscale', label: '黑白' },
  { id: 'invert', label: '反色' },
  { id: 'cool', label: '冷色调' },
  { id: 'warm', label: '暖色调' }
];

const predefinedColors = [
  'transparent', '#ffffff', '#000000', '#ff0000', '#00ff00', '#0000ff',
  '#ffff00', '#00ffff', '#ff00ff', '#808080', '#c0c0c0'
];

// 背景纹理数据
const predefinedPatterns = [
  { 
    id: 'checkerboard', 
    label: '棋盘格', 
    pattern: `
      <pattern id="checkerboard" patternUnits="userSpaceOnUse" width="20" height="20">
        <rect width="10" height="10" fill="#f3f4f6"/>
        <rect x="10" y="10" width="10" height="10" fill="#f3f4f6"/>
        <rect x="10" y="0" width="10" height="10" fill="#e5e7eb"/>
        <rect x="0" y="10" width="10" height="10" fill="#e5e7eb"/>
      </pattern>
    `
  },
  { 
    id: 'dots', 
    label: '圆点', 
    pattern: `
      <pattern id="dots" patternUnits="userSpaceOnUse" width="20" height="20">
        <rect width="20" height="20" fill="#f9fafb"/>
        <circle cx="5" cy="5" r="2" fill="#e5e7eb"/>
        <circle cx="15" cy="15" r="2" fill="#e5e7eb"/>
      </pattern>
    `
  },
  { 
    id: 'stripes', 
    label: '条纹', 
    pattern: `
      <pattern id="stripes" patternUnits="userSpaceOnUse" width="20" height="20">
        <rect width="20" height="20" fill="#f9fafb"/>
        <rect y="5" width="20" height="1" fill="#e5e7eb"/>
        <rect y="15" width="20" height="1" fill="#e5e7eb"/>
      </pattern>
    `
  },
  { 
    id: 'crosshatch', 
    label: '交叉线', 
    pattern: `
      <pattern id="crosshatch" patternUnits="userSpaceOnUse" width="20" height="20">
        <rect width="20" height="20" fill="#f9fafb"/>
        <path d="M0,0 L20,20" stroke="#e5e7eb" stroke-width="1"/>
        <path d="M20,0 L0,20" stroke="#e5e7eb" stroke-width="1"/>
      </pattern>
    `
  },
  { 
    id: 'zigzag', 
    label: '锯齿形', 
    pattern: `
      <pattern id="zigzag" patternUnits="userSpaceOnUse" width="20" height="10">
        <rect width="20" height="10" fill="#f9fafb"/>
        <path d="M0,0 L5,10 L10,0 L15,10 L20,0" stroke="#e5e7eb" stroke-width="1" fill="none"/>
      </pattern>
    `
  },
  { 
    id: 'grid', 
    label: '网格', 
    pattern: `
      <pattern id="grid" patternUnits="userSpaceOnUse" width="20" height="20">
        <rect width="20" height="20" fill="#f9fafb"/>
        <path d="M0,0 L0,20 M0,0 L20,0 M20,0 L20,20 M0,20 L20,20" stroke="#e5e7eb" stroke-width="1"/>
      </pattern>
    `
  },
  { 
    id: 'honeycomb', 
    label: '蜂窝', 
    pattern: `
      <pattern id="honeycomb" patternUnits="userSpaceOnUse" width="30" height="30">
        <rect width="30" height="30" fill="#f9fafb"/>
        <path d="M0,15 L7.5,5 L22.5,5 L30,15 L22.5,25 L7.5,25 Z" stroke="#e5e7eb" stroke-width="1" fill="none"/>
      </pattern>
    `
  },
  { 
    id: 'triangles', 
    label: '三角形', 
    pattern: `
      <pattern id="triangles" patternUnits="userSpaceOnUse" width="20" height="20">
        <rect width="20" height="20" fill="#f9fafb"/>
        <path d="M0,0 L10,17.3 L20,0 Z" stroke="#e5e7eb" stroke-width="1" fill="none"/>
      </pattern>
    `
  }
];

export function EditModal({ image, isOpen, onClose, onSave }: EditModalProps) {
  const [bgType, setBgType] = useState('color');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [bgPattern, setBgPattern] = useState('checkerboard');
  const [customBgImage, setCustomBgImage] = useState<File | null>(null);
  const [selectedEffect, setSelectedEffect] = useState('none');
  const [blurValue, setBlurValue] = useState(50);
  const [brightnessValue, setBrightnessValue] = useState(50);
  const [contrastValue, setContrastValue] = useState(50);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [exportUrl, setExportUrl] = useState('');
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);
  const [canvasRef, setCanvasRef] = useState<React.RefObject<HTMLCanvasElement> | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [canvasContainerRef, setCanvasContainerRef] = useState<React.RefObject<HTMLDivElement> | null>(null);
  const [canvasWrapperRef, setCanvasWrapperRef] = useState<React.RefObject<HTMLDivElement> | null>(null);
  const [filterValue, setFilterValue] = useState(50);

  const processedURL = image.processedFile ? URL.createObjectURL(image.processedFile) : '';

  useEffect(() => {
    // 清理函数，释放URL资源
    return () => {
      if (processedURL) {
        URL.revokeObjectURL(processedURL);
      }
    };
  }, [processedURL]);

  // 初始化预览
  useEffect(() => {
    if (image.processedFile) {
      // 只在初始化时应用一次，不随效果变化而变化
      initializePreview();
    }
  }, [image.processedFile]);

  // 初始化画布
  useEffect(() => {
    if (selectedEffect === 'brush' && canvasRef.current && image.processedFile) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        const img = new Image();
        img.src = processedURL;
        
        img.onload = () => {
          // 设置画布尺寸与图像一致
          canvas.width = img.width;
          canvas.height = img.height;
          
          // 绘制图像
          ctx.drawImage(img, 0, 0);
          
          // 保存原始图像数据
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          setOriginalImageData(imageData);
          setEditedImageData(imageData);
          
          // 调整容器高度以适应图像比例
          if (canvasContainerRef.current) {
            const containerWidth = canvasContainerRef.current.clientWidth;
            const aspectRatio = img.height / img.width;
            const idealHeight = containerWidth * aspectRatio;
            
            // 设置最小和最大高度限制
            const containerHeight = Math.min(Math.max(idealHeight, 400), 700);
            canvasContainerRef.current.style.height = `${containerHeight}px`;
          }
        };
      }
    }
  }, [selectedEffect, processedURL, image.processedFile]);

  // 初始化预览，只在图像加载时调用一次
  const initializePreview = async () => {
    if (!image.processedFile) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const img = new Image();
    img.src = processedURL;
    await new Promise(resolve => img.onload = resolve);
    
    canvas.width = img.width;
    canvas.height = img.height;
    
    // 绘制原始图像
    ctx.drawImage(img, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/png');
    setExportUrl(dataUrl);
  };

  // 修改useEffect钩子，确保滤镜强度变化时实时应用
  useEffect(() => {
    // 当相关状态变化时，自动应用更改
    if (image.processedFile) {
      const timer = setTimeout(() => {
        applyChanges();
      }, 300); // 添加防抖，避免频繁渲染
      
      return () => clearTimeout(timer);
    }
  }, [bgType, bgColor, bgPattern, customBgImage, selectedEffect, blurValue, brightnessValue, contrastValue, selectedFilter, filterValue]); // 添加filterValue到依赖数组

  // 获取当前效果的值
  const getCurrentEffectValue = () => {
    switch (selectedEffect) {
      case 'blur':
        return blurValue;
      case 'brightness':
        return brightnessValue;
      case 'contrast':
        return contrastValue;
      case 'filter':
        return filterValue;
      default:
        return 50;
    }
  };

  // 处理效果值变化
  const handleEffectValueChange = (value: number) => {
    switch (selectedEffect) {
      case 'blur':
        setBlurValue(value);
        break;
      case 'brightness':
        setBrightnessValue(value);
        break;
      case 'contrast':
        setContrastValue(value);
        break;
      case 'filter':
        setFilterValue(value);
        break;
    }
  };

  // 创建SVG背景纹理
  const createPatternBackground = (patternId: string) => {
    const svgStart = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <defs>`;
    
    const patternDef = predefinedPatterns.find(p => p.id === patternId)?.pattern || '';
    
    const svgEnd = `
      </defs>
      <rect width="100%" height="100%" fill="url(#${patternId})"/>
    </svg>`;
    
    return `url("data:image/svg+xml;base64,${btoa(svgStart + patternDef + svgEnd)}")`;
  };

  // 修改applyChanges函数，只在用户选择了背景类型时应用背景
  const applyChanges = async () => {
    if (!image.processedFile) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const img = new Image();
    img.src = processedURL;
    await new Promise(resolve => img.onload = resolve);
    
    canvas.width = img.width;
    canvas.height = img.height;
    
    // 只有当用户明确选择了背景类型时才应用背景
    if (bgType === 'color') {
      if (bgColor === 'transparent') {
        // 不做任何事情，保持透明背景
      } else {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    } else if (bgType === 'pattern' && bgPattern) {
      // 对于纹理背景，我们需要创建一个临时图像
      const patternImg = new Image();
      const patternSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
        <defs>
          ${predefinedPatterns.find(p => p.id === bgPattern)?.pattern || ''}
        </defs>
        <rect width="100" height="100" fill="url(#${bgPattern})"/>
      </svg>`;
      
      patternImg.src = 'data:image/svg+xml;base64,' + btoa(patternSvg);
      await new Promise(resolve => patternImg.onload = resolve);
      
      const pattern = ctx.createPattern(patternImg, 'repeat');
      if (pattern) {
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    } else if (bgType === 'image' && customBgImage) {
      const bgImg = new Image();
      bgImg.src = URL.createObjectURL(customBgImage);
      await new Promise(resolve => bgImg.onload = resolve);
      ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
    } else {
      // 如果没有选择背景，则使用透明背景
      // 不需要做任何事情，canvas默认是透明的
    }
    
    // 绘制处理后的图像
    ctx.drawImage(img, 0, 0);
    
    // Apply effects
    if (selectedEffect !== 'none') {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      switch (selectedEffect) {
        case 'blur':
          // Create a temporary canvas for blur effect
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');
          if (!tempCtx) break;
          
          tempCanvas.width = canvas.width;
          tempCanvas.height = canvas.height;
          
          // Draw current state to temp canvas
          tempCtx.drawImage(canvas, 0, 0);
          
          // Clear main canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          
          // Apply blur using CSS filter
          ctx.filter = `blur(${blurValue / 10}px)`;
          ctx.drawImage(tempCanvas, 0, 0);
          ctx.filter = 'none';
          break;
          
        case 'brightness':
          for (let i = 0; i < data.length; i += 4) {
            data[i] = Math.min(255, data[i] * (brightnessValue / 50));
            data[i + 1] = Math.min(255, data[i + 1] * (brightnessValue / 50));
            data[i + 2] = Math.min(255, data[i + 2] * (brightnessValue / 50));
          }
          ctx.putImageData(imageData, 0, 0);
          break;
          
        case 'contrast':
          const factor = (259 * (contrastValue + 255)) / (255 * (259 - contrastValue));
          for (let i = 0; i < data.length; i += 4) {
            data[i] = factor * (data[i] - 128) + 128;
            data[i + 1] = factor * (data[i + 1] - 128) + 128;
            data[i + 2] = factor * (data[i + 2] - 128) + 128;
          }
          ctx.putImageData(imageData, 0, 0);
          break;
          
        case 'filter':
          // 应用滤镜效果
          if (selectedFilter !== 'none') {
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) break;
            
            tempCanvas.width = canvas.width;
            tempCanvas.height = canvas.height;
            
            // 绘制当前状态到临时画布
            tempCtx.drawImage(canvas, 0, 0);
            
            // 清除主画布
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // 应用滤镜，使用filterValue调整强度
            const intensity = filterValue / 50; // 将0-100的值转换为0-2的比例
            
            switch (selectedFilter) {
              case 'sepia':
                ctx.filter = `sepia(${intensity * 100}%)`;
                break;
              case 'grayscale':
                ctx.filter = `grayscale(${intensity * 100}%)`;
                break;
              case 'invert':
                ctx.filter = `invert(${intensity * 100}%)`;
                break;
              case 'cool':
                ctx.filter = `hue-rotate(${intensity * 180}deg)`;
                break;
              case 'warm':
                ctx.filter = `sepia(${intensity * 50}%) saturate(${100 + intensity * 50}%) hue-rotate(${-30 * intensity}deg)`;
                break;
            }
            
            ctx.drawImage(tempCanvas, 0, 0);
            ctx.filter = 'none';
          }
          break;
      }
    }
    
    // 导出处理后的图像
    const dataUrl = canvas.toDataURL('image/png');
    setExportUrl(dataUrl);
  };

  const handleSave = async () => {
    // 应用所有效果并保存
    await applyChanges();
    onSave(exportUrl);
    onClose();
  };

  // 处理缩放
  const handleZoom = (direction: 'in' | 'out') => {
    if (direction === 'in') {
      setZoomLevel(prev => Math.min(prev + 0.25, 5)); // 最大放大5倍
    } else {
      setZoomLevel(prev => Math.max(prev - 0.25, 0.5)); // 最小缩小0.5倍
    }
  };

  // 重置缩放和位置
  const resetZoom = () => {
    setZoomLevel(1);
  };

  if (!isOpen) return null;

  // 创建透明背景网格的CSS
  const transparentBgCSS = `
    background-image: 
      linear-gradient(45deg, #ccc 25%, transparent 25%),
      linear-gradient(-45deg, #ccc 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #ccc 75%),
      linear-gradient(-45deg, transparent 75%, #ccc 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    background-color: white;
  `;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-xl p-6 ${selectedEffect === 'brush' ? 'max-w-5xl' : 'max-w-4xl'} w-full max-h-[90vh] overflow-y-auto shadow-xl`}>
        <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
          <h2 className="text-xl font-semibold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent flex items-center">
            <i className="fas fa-wand-magic-sparkles text-pink-500 mr-2"></i>
            编辑图片
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className={`grid ${selectedEffect === 'brush' ? 'grid-cols-3' : 'grid-cols-2'} gap-6`}>
          <div className={`space-y-6 ${selectedEffect === 'brush' ? 'col-span-1' : 'col-span-1'}`}>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-700 mb-3 flex items-center">
                <i className="fas fa-palette text-purple-500 mr-2"></i>
                背景
              </h3>
              <div className="flex gap-2 mb-4">
                {backgroundOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => setBgType(option.id)}
                    className={`px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors ${
                      bgType === option.id
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-md'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-pink-300 hover:text-pink-500'
                    }`}
                  >
                    <i className={`fas ${option.icon}`}></i>
                    {option.label}
                  </button>
                ))}
              </div>

              {bgType === 'color' && (
                <div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {predefinedColors.map(color => (
                      <button
                        key={color}
                        onClick={() => setBgColor(color)}
                        className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${bgColor === color ? 'border-pink-500 transform scale-110' : 'border-gray-200'}`}
                        style={{ 
                          backgroundColor: color === 'transparent' ? 'transparent' : color,
                          backgroundImage: color === 'transparent' ? 
                            'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' : 
                            'none',
                          backgroundSize: '10px 10px',
                          backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px'
                        }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => setShowCustomColorPicker(!showCustomColorPicker)}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:border-pink-300 hover:text-pink-500 transition-colors text-sm text-gray-700 flex items-center gap-1"
                    >
                      <i className="fas fa-eye-dropper text-purple-500"></i>
                      自定义颜色
                    </button>
                    {showCustomColorPicker && (
                      <input
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="w-8 h-8 border-0 rounded-md cursor-pointer"
                      />
                    )}
                  </div>
                </div>
              )}

              {bgType === 'pattern' && (
                <div className="grid grid-cols-4 gap-2">
                  {predefinedPatterns.map(pattern => (
                    <button
                      key={pattern.id}
                      onClick={() => setBgPattern(pattern.id)}
                      className={`aspect-square rounded-md border-2 transition-transform hover:scale-105 overflow-hidden ${
                        bgPattern === pattern.id ? 'border-pink-500 transform scale-105' : 'border-gray-200'
                      }`}
                      style={{ 
                        backgroundImage: createPatternBackground(pattern.id),
                        backgroundSize: 'cover'
                      }}
                      title={pattern.label}
                    >
                      <div className="w-full h-full flex items-center justify-center">
                        {bgPattern === pattern.id && (
                          <div className="w-5 h-5 rounded-full bg-pink-500 text-white flex items-center justify-center">
                            <i className="fas fa-check text-xs"></i>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {bgType === 'image' && (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-pink-300 transition-colors">
                  <i className="fas fa-cloud-arrow-up text-2xl text-pink-400 mb-2"></i>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCustomBgImage(e.target.files?.[0] || null)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-2">支持 JPG、PNG 格式</p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-700 mb-3 flex items-center">
                <i className="fas fa-sliders text-purple-500 mr-2"></i>
                效果
              </h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {effectOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => setSelectedEffect(option.id)}
                    className={`px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${
                      selectedEffect === option.id
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-md'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-pink-300 hover:text-pink-500'
                    }`}
                  >
                    <i className={`fas ${option.icon}`}></i>
                    {option.label}
                  </button>
                ))}
              </div>

              {selectedEffect !== 'none' && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`fas ${selectedEffect === 'blur' ? 'fa-droplet-slash' : selectedEffect === 'brightness' ? 'fa-sun' : selectedEffect === 'contrast' ? 'fa-circle-half-stroke' : selectedEffect === 'filter' ? 'fa-photo-film' : 'fa-circle-half-stroke'} text-pink-500`}></i>
                    <span className="text-sm font-medium text-gray-700">调整强度</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={getCurrentEffectValue()}
                    onChange={(e) => handleEffectValueChange(Number(e.target.value))}
                    className="w-full accent-pink-500"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>0</span>
                    <span className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded-full text-xs">
                      {getCurrentEffectValue()}
                    </span>
                    <span>100</span>
                  </div>
                  
                  {selectedEffect === 'filter' && (
                    <div className="mt-4">
                      <div className="text-sm font-medium text-gray-700 mb-2">选择滤镜</div>
                      <div className="grid grid-cols-3 gap-2">
                        {filterOptions.map(option => (
                          <button
                            key={option.id}
                            onClick={() => setSelectedFilter(option.id)}
                            className={`px-3 py-2 rounded-md text-sm flex items-center justify-center gap-1 transition-colors ${
                              selectedFilter === option.id
                                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-sm'
                                : 'bg-white border border-gray-200 text-gray-700 hover:border-pink-300 hover:text-pink-500'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={`${selectedEffect === 'brush' ? 'col-span-2' : 'col-span-1'}`}>
            <h3 className="font-medium text-gray-700 mb-3 flex items-center">
              <i className="fas fa-eye text-purple-500 mr-2"></i>
              预览
            </h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden p-4" style={{ background: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CiAgPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZjNmNGY2Ii8+CiAgPHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNmM2Y0ZjYiLz4KICA8cmVjdCB4PSIxMCIgeT0iMCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZTVlN2ViIi8+CiAgPHJlY3QgeD0iMCIgeT0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2U1ZTdlYiIvPgo8L3N2Zz4=")' }}>
              {selectedEffect === 'brush' ? (
                <div 
                  className="relative overflow-hidden" 
                  ref={canvasContainerRef}
                  style={{ 
                    minHeight: '400px', 
                    maxHeight: '700px' 
                  }}
                >
                  <div 
                    ref={canvasWrapperRef}
                    style={{ 
                      transform: `scale(${zoomLevel})`, 
                      transformOrigin: 'top left', 
                      height: '100%',
                      width: '100%',
                      overflow: 'visible'
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      className="object-contain rounded-md"
                      onMouseDown={(e) => {
                        if (e.button === 1 || (e.button === 0 && e.altKey)) {
                          // 这是拖动操作，已在容器上处理
                          return;
                        }
                        
                        if (canvasRef.current) {
                          const canvas = canvasRef.current;
                          const ctx = canvas.getContext('2d');
                          if (ctx) {
                            ctx.beginPath();
                            ctx.moveTo(e.clientX, e.clientY);
                          }
                        }
                      }}
                      onMouseMove={(e) => {
                        if (canvasRef.current) {
                          const canvas = canvasRef.current;
                          const ctx = canvas.getContext('2d');
                          if (ctx) {
                            ctx.lineTo(e.clientX, e.clientY);
                            ctx.strokeStyle = 'rgba(255,255,255,0.9)';
                            ctx.lineWidth = 5;
                            ctx.lineCap = 'round';
                            ctx.lineJoin = 'round';
                            ctx.stroke();
                          }
                        }
                      }}
                      onMouseUp={(e) => {
                        if (e.button === 1 || (e.button === 0 && e.altKey)) {
                          // 这是拖动操作，已在容器上处理
                          return;
                        }
                        
                        if (canvasRef.current) {
                          const canvas = canvasRef.current;
                          const ctx = canvas.getContext('2d');
                          if (ctx) {
                            ctx.closePath();
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              ) : (
              <img
                src={exportUrl || processedURL}
                alt="预览"
                className="w-full object-contain rounded-md"
              />
              )}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start">
                <i className="fas fa-lightbulb text-yellow-500 mt-0.5 mr-2"></i>
                <div>
                  <p className="text-sm text-gray-700 font-medium">提示</p>
                  <p className="text-xs text-gray-600 mt-1">
                    您可以选择纯色、纹理或自定义图片作为背景，并应用各种效果来美化您的图片。
                    所有处理在本地完成，保障您的隐私安全。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors flex items-center gap-1"
          >
            <i className="fas fa-times text-gray-400"></i>
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:opacity-90 transition-opacity shadow-md flex items-center gap-1"
          >
            <i className="fas fa-check"></i>
            保存更改
          </button>
        </div>
      </div>
    </div>
  );
}
